import { getInstance } from '@/utils/mapbox/instanceRegistry'

// Single opaque `m` query param that round-trips the live map view so a copied URL
// reproduces exactly what the sharer sees: camera per instance (center, zoom, bearing,
// pitch), the split-screen layout, and each map's basemap. Framework-agnostic on purpose
// (mirrors instanceRegistry): components call into it, it never touches Vuex.

const PARAM = 'm'
const GLOBAL_KEY = '__global__'
const WRITE_DEBOUNCE_MS = 300

type Camera = [number, number, number, number, number] // lng, lat, zoom, bearing, pitch

interface MapUrlPayload {
  v: number
  tab?: string
  cam?: Record<string, Camera>
  split?: { t: string[]; a: string; d: [number, number]; off: string[] }
  base?: Record<string, string>
  // Per-tile basemap config: PlanetScope mosaic selection (`ps`) + Mapbox view
  // toggles (`lbl`/`bld`/`ter`), only the keys that differ from defaults.
  cfg?: Record<string, any>
}

let pending: MapUrlPayload | null = null
let hydrated = false
let writeTimer: ReturnType<typeof setTimeout> | null = null
// activeTab is route-derived (useActiveTab), not a Vuex getter — App.vue keeps this in
// sync so writes from anywhere (incl. MapCanvas) can stamp the current view.
let currentTab: string | undefined

export const setCurrentTab = (tab: string | undefined): void => { currentTab = tab }

const toBase64Url = (str: string): string =>
  btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

const fromBase64Url = (str: string): string => {
  const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4))
  return atob(str.replace(/-/g, '+').replace(/_/g, '/') + pad)
}

export const encodeState = (payload: MapUrlPayload): string =>
  toBase64Url(JSON.stringify(payload))

const isObj = (x: any): x is Record<string, any> => !!x && typeof x === 'object' && !Array.isArray(x)

// The `m` param is user-controlled, so a decoded payload is untrusted: require an
// object with a numeric version, and let the pending* getters validate each field's
// shape before hydration touches it (a malformed split/cfg must never throw on load).
export const decodeState = (str: string): MapUrlPayload | null => {
  try {
    const parsed = JSON.parse(fromBase64Url(str))
    return isObj(parsed) && typeof parsed.v === 'number' ? (parsed as MapUrlPayload) : null
  } catch {
    return null
  }
}

// Parse the incoming link once, before anything writes. Called at app mount.
export const initFromUrl = (): void => {
  const raw = new URLSearchParams(window.location.search).get(PARAM)
  pending = raw ? decodeState(raw) : null
  hydrated = false
}

export const hasPending = (): boolean => pending !== null && !hydrated
export const pendingTab = (): string | undefined =>
  typeof pending?.tab === 'string' ? pending.tab : undefined

// Only return a split when it's fully well-formed — hydration indexes t/d/off
// directly, so a partial object would throw on a shared link.
export const pendingSplit = () => {
  const s = pending?.split
  if (!isObj(s) || !Array.isArray(s.t) || !s.t.every(x => typeof x === 'string')) return undefined
  if (typeof s.a !== 'string') return undefined
  if (!Array.isArray(s.d) || typeof s.d[0] !== 'number' || typeof s.d[1] !== 'number') return undefined
  if (!Array.isArray(s.off) || !s.off.every(x => typeof x === 'string')) return undefined
  return s
}

// Keep only string basemap keys.
export const pendingBasemaps = (): Record<string, string> => {
  const base = pending?.base
  if (!isObj(base)) return {}
  return Object.fromEntries(Object.entries(base).filter(([, v]) => typeof v === 'string'))
}

// Keep only object-shaped per-tile config entries.
export const pendingBasemapConfig = (): Record<string, any> => {
  const cfg = pending?.cfg
  if (!isObj(cfg)) return {}
  return Object.fromEntries(Object.entries(cfg).filter(([, v]) => isObj(v)))
}
export const markHydrated = (): void => { hydrated = true }

// Return and consume a pending camera for an instance key, so it applies exactly once
// as that canvas/pane mounts.
export const takePendingCamera = (key: string): Camera | null => {
  const cam = pending?.cam?.[key]
  if (!cam) return null
  delete pending!.cam![key]
  return cam
}

const readCamera = (inst: any): Camera => {
  const c = inst.getCenter()
  return [c.lng, c.lat, inst.getZoom(), inst.getBearing(), inst.getPitch()]
}

// Per-tile basemap config for the hash: the PlanetScope selection (only when the
// tile is on PlanetScope) plus any non-default Mapbox view toggles. Returns null
// when there's nothing worth serializing, to keep the hash small.
const tileConfig = (store: any, id: string, basemap?: string): Record<string, any> | null => {
  const cfg: Record<string, any> = {}
  if (basemap === 'planetscope') {
    const ps = store.getters['layers/getPlanetscopeSelection'](id)
    if (ps) cfg.ps = ps
  }
  const s = store.getters['layers/getMapViewSettings'](id)
  if (s) {
    if (s.labels === false) cfg.lbl = false
    if (s.buildings) cfg.bld = true
    if (s.terrain) cfg.ter = true
  }
  return Object.keys(cfg).length ? cfg : null
}

const buildPayload = (store: any): MapUrlPayload => {
  const orgId = store.getters['org/getSelected']
  const tiles: string[] = orgId ? store.getters['split/getTiles'](orgId) : []
  const maps: any[] = orgId ? store.getters['maps/getMaps'](orgId) : []

  const cam: Record<string, Camera> = {}
  for (const key of [GLOBAL_KEY, ...tiles]) {
    const inst = getInstance(key)
    if (inst) cam[key] = readCamera(inst)
  }

  const base: Record<string, string> = {}
  const cfg: Record<string, any> = {}
  for (const id of tiles) {
    const m = maps.find(mp => mp.id === id)
    if (m?.basemap) base[id] = m.basemap
    const c = tileConfig(store, id, m?.basemap)
    if (c) cfg[id] = c
  }

  const payload: MapUrlPayload = { v: 1, tab: currentTab, cam }
  if (tiles.length) {
    const { x, y } = store.getters['split/getDividers'](orgId)
    payload.split = {
      t: tiles,
      a: store.getters['split/getArrangement'](orgId),
      d: [x, y],
      off: tiles.filter(id => !store.getters['split/isSyncEnabled'](id)),
    }
    payload.base = base
    if (Object.keys(cfg).length) payload.cfg = cfg
  }
  return payload
}

const flushWriteUrl = (store: any): void => {
  const payload = buildPayload(store)
  const params = new URLSearchParams(window.location.search)
  params.set(PARAM, encodeState(payload))
  window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
}

// Debounced URL write. Suppressed until hydration completes so restoring an incoming
// link never overwrites it before the panes have mounted.
export const scheduleWriteUrl = (store: any): void => {
  if (pending && !hydrated) return
  if (writeTimer) clearTimeout(writeTimer)
  writeTimer = setTimeout(() => {
    writeTimer = null
    flushWriteUrl(store)
  }, WRITE_DEBOUNCE_MS)
}
